import type {Content, ImageField, KeyTextField, LinkField} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next/pages'
import cn from 'classnames'
import type {LinkResolverFn} from 'prismicio'
import {BioSection} from './components/BioSection'
import {CTASection} from './components/CTASection'
import {DrawerText} from './components/DrawerText'
import {DrawerVideo} from './components/DrawerVideo'

type StresstimonialsDrawerContentProps = {
  video_source?: LinkField
  fallback_image: ImageField
  drawer_title?: string | null
  drawer_bio_title?: string | null
  bio_info?: Array<{name: string | null; value: string | null}>
  free_text?: Content.StresstimonialsCardSliceDefaultPrimary['free_text']
  drawer_image?: ImageField
  free_text_2?: Content.StresstimonialsCardSliceDefaultPrimary['free_text']
  cta_eyebrow?: string | null
  cta_title?: string | null
  cta_description?: string | null
  primaryButtonData?: {
    link: LinkField
    marketing_id?: KeyTextField
    intent?: KeyTextField
    label?: KeyTextField
  } | null
  linkResolver: LinkResolverFn
  componentIndex: number
  pageUid: string
  componentName: string
}

export const StresstimonialsDrawerContent = ({
  video_source,
  fallback_image,
  drawer_title,
  drawer_bio_title,
  bio_info,
  free_text,
  drawer_image,
  free_text_2,
  cta_eyebrow,
  cta_title,
  cta_description,
  primaryButtonData,
  linkResolver,
  componentIndex,
  pageUid,
  componentName,
}: StresstimonialsDrawerContentProps) => (
  <div
    className="flex min-h-full w-full flex-col items-center bg-moss-white p-5 pb-[100px] sm:w-[720px]"
    style={{
      backgroundImage: 'url(/icons/noise-texture.svg)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundAttachment: 'fixed',
    }}
  >
    <div className="flex w-full flex-col gap-6 sm:max-w-none md:gap-2.5">
      <DrawerVideo
        video_source={video_source}
        pageUid={pageUid}
        componentName={componentName}
        fallback_image={fallback_image}
        drawerTitle={drawer_title || undefined}
      />

      <div
        className={cn('flex w-full flex-col gap-6 p-0 md:p-[30px] md:pb-[50px] md:pr-[60px]', {
          'md:pr-[80px]': !isFilled.link(video_source),
        })}
      >
        {isFilled.keyText(drawer_title) && (
          <h2
            className={cn(
              'text-[38px] font-medium leading-normal tracking-[-0.42px] text-moss-black sm:text-40 sm:tracking-[-0.36px]',
              {
                'pr-[38px] sm:pr-0': !isFilled.link(video_source),
              },
            )}
          >
            {drawer_title}
          </h2>
        )}

        <BioSection drawer_bio_title={drawer_bio_title} bio_info={bio_info} />

        <DrawerText text={free_text} />

        {isFilled.image(drawer_image) && (
          <div className="my-2.5 w-full overflow-hidden rounded-lg">
            <PrismicNextImage field={drawer_image} className="h-auto w-full object-cover" />
          </div>
        )}

        <DrawerText text={free_text_2} />

        <CTASection
          cta_eyebrow={cta_eyebrow}
          cta_title={cta_title}
          cta_description={cta_description}
          primaryButtonData={primaryButtonData}
          linkResolver={linkResolver}
          componentIndex={componentIndex}
          pageUid={pageUid}
          componentName={componentName}
        />
      </div>
    </div>
  </div>
)
